import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { C, Btn, Input } from "../components/UI";
import { supabase } from "../lib/supabase";

// Monta o texto do periodo a partir das datas do formulario (ex: "03/2020 - atual")
function formatarPeriodo(dataInicio, dataFim, atual) {
  const fmt = (v) => {
    if (!v) return "";
    const partes = String(v).split("-");
    const ano = partes[0];
    const mes = partes[1];
    return mes ? `${mes}/${ano}` : ano;
  };
  const ini = fmt(dataInicio);
  const fim = atual ? "atual" : fmt(dataFim);
  if (ini && fim) return `${ini} - ${fim}`;
  return ini || fim || null;
}

export default function CompletarCurriculo() {
  const navigate = useNavigate();
  const [etapa, setEtapa] = useState(1); // 1: Dados pessoais, 2: Formacao, 3: Experiencias, 4: Finalizar

  // Estado do salvamento
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  // Dados pessoais
  const [dadosPessoais, setDadosPessoais] = useState({
    telefone: "",
    dataNascimento: "",
    linkedin: "",
    portfolio: "",
    sobre: ""
  });

  // Formacao academica
  const [formacoes, setFormacoes] = useState([{
    instituicao: "",
    curso: "",
    nivel: "graduacao",
    anoInicio: "",
    anoConclusao: "",
    status: "concluido"
  }]);

  // Experiencias profissionais (com avaliacao obrigatoria)
  const [experiencias, setExperiencias] = useState([{
    empresa: "",
    cargo: "",
    dataInicio: "",
    dataFim: "",
    atual: false,
    descricao: "",
    // Avaliacao obrigatoria
    avaliacao: {
      nota: 0,
      comentario: "",
      pontoPositivo: "",
      pontoNegativo: "",
      recomenda: null,
      valores: [],
      avaliado: false
    }
  }]);

  const handleDadosPessoaisChange = (e) => {
    setDadosPessoais({ ...dadosPessoais, [e.target.name]: e.target.value });
  };

  const handleFormacaoChange = (index, field, value) => {
    const novasFormacoes = [...formacoes];
    novasFormacoes[index][field] = value;
    setFormacoes(novasFormacoes);
  };

  const addFormacao = () => {
    setFormacoes([...formacoes, {
      instituicao: "",
      curso: "",
      nivel: "graduacao",
      anoInicio: "",
      anoConclusao: "",
      status: "concluido"
    }]);
  };

  const removeFormacao = (index) => {
    if (formacoes.length > 1) {
      setFormacoes(formacoes.filter((_, i) => i !== index));
    }
  };

  const handleExperienciaChange = (index, field, value) => {
    const novasExperiencias = [...experiencias];
    novasExperiencias[index][field] = value;
    setExperiencias(novasExperiencias);
  };

  const handleAvaliacaoChange = (expIndex, field, value) => {
    const novasExperiencias = [...experiencias];
    novasExperiencias[expIndex].avaliacao[field] = value;
    setExperiencias(novasExperiencias);
  };

  const marcarAvaliado = (expIndex) => {
    const novasExperiencias = [...experiencias];
    novasExperiencias[expIndex].avaliacao.avaliado = true;
    setExperiencias(novasExperiencias);
  };

  const addExperiencia = () => {
    setExperiencias([...experiencias, {
      empresa: "",
      cargo: "",
      dataInicio: "",
      dataFim: "",
      atual: false,
      descricao: "",
      avaliacao: {
        nota: 0,
        comentario: "",
        pontoPositivo: "",
        pontoNegativo: "",
        recomenda: null,
        valores: [],
        avaliado: false
      }
    }]);
  };

  const removeExperiencia = (index) => {
    if (experiencias.length > 1) {
      setExperiencias(experiencias.filter((_, i) => i !== index));
    }
  };

  const todasExperienciasAvaliadas = () => {
    return experiencias.every(exp => exp.avaliacao.avaliado && exp.avaliacao.nota > 0);
  };

  const podeFinalizar = () => {
    return experiencias.length > 0 && todasExperienciasAvaliadas();
  };

  const finalizarCadastro = async () => {
    if (!podeFinalizar()) {
      alert("Voce precisa avaliar todas as empresas onde trabalhou antes de finalizar o cadastro.");
      return;
    }

    setErro("");
    setSalvando(true);

    try {
      // Quem esta logada
      const { data: sessao, error: erroSessao } = await supabase.auth.getUser();
      if (erroSessao || !sessao || !sessao.user) {
        throw new Error("Sua sessao expirou. Faca login novamente para salvar seu cadastro.");
      }
      const userId = sessao.user.id;

      // Linha dela na tabela mulheres
      const { data: mulheresEncontradas, error: erroMulher } = await supabase
        .from("mulheres")
        .select("id")
        .eq("user_id", userId)
        .limit(1);
      if (erroMulher) throw erroMulher;
      if (!mulheresEncontradas || mulheresEncontradas.length === 0) {
        throw new Error("Nao encontramos seu perfil de usuaria. Entre em contato com o suporte antes de continuar.");
      }
      const mulherId = mulheresEncontradas[0].id;

      // 1) Dados pessoais
      const { error: erroPerfil } = await supabase
        .from("mulheres")
        .update({
          telefone: dadosPessoais.telefone || null,
          data_nascimento: dadosPessoais.dataNascimento || null,
          linkedin: dadosPessoais.linkedin || null,
          portfolio: dadosPessoais.portfolio || null,
          sobre: dadosPessoais.sobre || null
        })
        .eq("id", mulherId);
      if (erroPerfil) throw erroPerfil;

      // 2) Formacoes (substituem as anteriores)
      const formacoesValidas = formacoes.filter(
        f => (f.instituicao || "").trim() || (f.curso || "").trim()
      );
      const { error: erroApagaFormacoes } = await supabase
        .from("formacoes")
        .delete()
        .eq("mulher_id", mulherId);
      if (erroApagaFormacoes) throw erroApagaFormacoes;

      if (formacoesValidas.length > 0) {
        const { error: erroInsereFormacoes } = await supabase
          .from("formacoes")
          .insert(formacoesValidas.map(f => ({
            mulher_id: mulherId,
            instituicao: f.instituicao || null,
            curso: f.curso || null,
            nivel: f.nivel || null,
            ano_inicio: f.anoInicio ? Number(f.anoInicio) : null,
            ano_conclusao: f.anoConclusao ? Number(f.anoConclusao) : null,
            status: f.status || null
          })));
        if (erroInsereFormacoes) throw erroInsereFormacoes;
      }

      // 3) Experiencias e avaliacoes
      const experienciasValidas = experiencias.filter(e => (e.empresa || "").trim());
      if (experienciasValidas.length === 0) {
        throw new Error("Informe pelo menos uma empresa onde voce trabalhou.");
      }

      const { error: erroApagaExperiencias } = await supabase
        .from("experiencias")
        .delete()
        .eq("mulher_id", mulherId);
      if (erroApagaExperiencias) throw erroApagaExperiencias;

      const avisos = [];

      for (const exp of experienciasValidas) {
        const nomeEmpresa = exp.empresa.trim();

        // Acha a empresa pelo nome, ou cria como pendente
        const { data: empresaId, error: erroEmpresa } = await supabase
          .rpc("womom_empresa_por_nome", { p_nome: nomeEmpresa });
        if (erroEmpresa) throw erroEmpresa;

        // Guarda a experiencia no curriculo
        const { error: erroExperiencia } = await supabase
          .from("experiencias")
          .insert({
            mulher_id: mulherId,
            empresa_id: empresaId,
            empresa_nome: nomeEmpresa,
            cargo: exp.cargo || null,
            data_inicio: exp.dataInicio || null,
            data_fim: exp.atual ? null : (exp.dataFim || null),
            atual: !!exp.atual,
            descricao: exp.descricao || null
          });
        if (erroExperiencia) throw erroExperiencia;

        // Guarda a avaliacao, para moderacao
        const a = exp.avaliacao || {};
        const positivos = (a.pontoPositivo || "").trim() ? [a.pontoPositivo.trim()] : [];
        const negativos = (a.pontoNegativo || "").trim() ? [a.pontoNegativo.trim()] : [];

        const { error: erroAvaliacao } = await supabase
          .from("avaliacoes")
          .insert({
            mulher_id: mulherId,
            empresa_id: empresaId,
            nota_geral: a.nota || null,
            comentario: a.comentario || null,
            cargo: exp.cargo || null,
            periodo: formatarPeriodo(exp.dataInicio, exp.dataFim, exp.atual),
            pontos_positivos: positivos,
            pontos_negativos: negativos,
            recomenda: (a.recomenda === null || a.recomenda === undefined) ? null : a.recomenda,
            valores: Array.isArray(a.valores) ? a.valores : [],
            anonima: true,
            status: "pendente"
          });

        if (erroAvaliacao) {
          // 23505 = ja existe avaliacao dessa mulher para essa empresa
          if (erroAvaliacao.code === "23505") {
            avisos.push(`Voce ja havia avaliado ${nomeEmpresa}. Mantivemos a avaliacao anterior.`);
          } else {
            throw erroAvaliacao;
          }
        }
      }

      if (avisos.length > 0) {
        alert(avisos.join("\n"));
      }

      navigate("/minhas-avaliacoes");
    } catch (e) {
      console.error("Erro ao salvar o curriculo:", e);
      const mensagem =
        (e && e.message) ||
        "Nao conseguimos salvar seus dados agora. Tente novamente em alguns instantes.";
      setErro(mensagem);
    } finally {
      setSalvando(false);
    }
  };

  const etapas = [
    { numero: 1, titulo: "Dados Pessoais" },
    { numero: 2, titulo: "Formacao Academica" },
    { numero: 3, titulo: "Experiencias e Avaliacoes" },
    { numero: 4, titulo: "Finalizar" }
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.background }}>
      {/* Header */}
      <section style={{
        background: C.roseMid,
        padding: "40px 20px",
        textAlign: "center"
      }}>
        <h1 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 32,
          fontWeight: 700,
          color: "#fff",
          marginBottom: 10
        }}>
          Complete seu Curriculo
        </h1>
        <p style={{ color: "rgba(255,255,255,0.85)", fontSize: 15 }}>
          Preencha suas informacoes para encontrar as melhores oportunidades
        </p>
      </section>

      {/* Progress bar */}
      <div style={{
        maxWidth: 800,
        margin: "0 auto",
        padding: "30px 20px 0",
        display: "flex",
        justifyContent: "space-between",
        position: "relative"
      }}>
        {/* Linha de fundo */}
        <div style={{
          position: "absolute",
          top: 45,
          left: "10%",
          right: "10%",
          height: 3,
          background: C.border,
          zIndex: 0
        }} />
        {/* Linha de progresso */}
        <div style={{
          position: "absolute",
          top: 45,
          left: "10%",
          width: `${((etapa - 1) / 3) * 80}%`,
          height: 3,
          background: C.roseMid,
          zIndex: 1,
          transition: "width 0.3s ease"
        }} />

        {etapas.map((e) => (
          <div key={e.numero} style={{ textAlign: "center", zIndex: 2 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: etapa >= e.numero ? C.roseMid : C.surface,
              color: etapa >= e.numero ? "#fff" : C.textSec,
              border: `2px solid ${etapa >= e.numero ? C.roseMid : C.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 600,
              fontSize: 14,
              margin: "0 auto 8px",
              transition: "all 0.3s ease"
            }}>
              {etapa > e.numero ? "✓" : e.numero}
            </div>
            <span style={{
              fontSize: 12,
              color: etapa >= e.numero ? C.roseMid : C.textSec,
              fontWeight: etapa === e.numero ? 600 : 400
            }}>
              {e.titulo}
            </span>
          </div>
        ))}
      </div>

      {/* Conteudo */}
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 20px 60px" }}>

        {/* ETAPA 1: Dados Pessoais */}
        {etapa === 1 && (
          <div style={{
            background: C.surface,
            borderRadius: 16,
            padding: 32,
            border: `1px solid ${C.border}`
          }}>
            <h2 style={{ fontSize: 22, fontWeight: 600, color: C.textMain, marginBottom: 24 }}>
              Informacoes Pessoais
            </h2>

            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <Input
                  label="Telefone"
                  name="telefone"
                  value={dadosPessoais.telefone}
                  onChange={handleDadosPessoaisChange}
                  placeholder="(11) 99999-9999"
                />
                <Input
                  label="Data de nascimento"
                  name="dataNascimento"
                  type="date"
                  value={dadosPessoais.dataNascimento}
                  onChange={handleDadosPessoaisChange}
                />
              </div>

              <Input
                label="LinkedIn"
                name="linkedin"
                value={dadosPessoais.linkedin}
                onChange={handleDadosPessoaisChange}
                placeholder="https://linkedin.com/in/seu-perfil"
              />

              <Input
                label="Portfolio ou site pessoal"
                name="portfolio"
                value={dadosPessoais.portfolio}
                onChange={handleDadosPessoaisChange}
                placeholder="https://seusite.com"
              />

              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: C.textSec, marginBottom: 6 }}>
                  Sobre voce
                </label>
                <textarea
                  name="sobre"
                  value={dadosPessoais.sobre}
                  onChange={handleDadosPessoaisChange}
                  rows={4}
                  placeholder="Conte um pouco sobre voce, seus objetivos e o que busca na carreira..."
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    fontSize: 15,
                    resize: "vertical",
                    fontFamily: "inherit"
                  }}
                />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 32 }}>
              <Btn variant="primary" onClick={() => setEtapa(2)} style={{ padding: "12px 32px" }}>
                Proximo
              </Btn>
            </div>
          </div>
        )}

        {/* ETAPA 2: Formacao Academica */}
        {etapa === 2 && (
          <div style={{
            background: C.surface,
            borderRadius: 16,
            padding: 32,
            border: `1px solid ${C.border}`
          }}>
            <h2 style={{ fontSize: 22, fontWeight: 600, color: C.textMain, marginBottom: 24 }}>
              Formacao Academica
            </h2>

            {formacoes.map((formacao, index) => (
              <div key={index} style={{
                padding: 24,
                background: C.background,
                borderRadius: 12,
                marginBottom: 20,
                position: "relative"
              }}>
                {formacoes.length > 1 && (
                  <button
                    onClick={() => removeFormacao(index)}
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      background: "#FEE2E2",
                      color: "#991B1B",
                      border: "none",
                      borderRadius: 6,
                      padding: "4px 10px",
                      fontSize: 12,
                      cursor: "pointer"
                    }}
                  >
                    Remover
                  </button>
                )}

                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <Input
                    label="Instituicao de ensino"
                    value={formacao.instituicao}
                    onChange={(e) => handleFormacaoChange(index, "instituicao", e.target.value)}
                    placeholder="Nome da universidade/escola"
                  />
                  <Input
                    label="Curso"
                    value={formacao.curso}
                    onChange={(e) => handleFormacaoChange(index, "curso", e.target.value)}
                    placeholder="Nome do curso"
                  />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: C.textSec, marginBottom: 6 }}>
                        Nivel
                      </label>
                      <select
                        value={formacao.nivel}
                        onChange={(e) => handleFormacaoChange(index, "nivel", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "12px 14px",
                          border: `1px solid ${C.border}`,
                          borderRadius: 10,
                          fontSize: 15,
                          background: C.surface
                        }}
                      >
                        <option value="tecnico">Tecnico</option>
                        <option value="graduacao">Graduacao</option>
                        <option value="pos-graduacao">Pos-graduacao</option>
                        <option value="mestrado">Mestrado</option>
                        <option value="doutorado">Doutorado</option>
                        <option value="mba">MBA</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: C.textSec, marginBottom: 6 }}>
                        Status
                      </label>
                      <select
                        value={formacao.status}
                        onChange={(e) => handleFormacaoChange(index, "status", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "12px 14px",
                          border: `1px solid ${C.border}`,
                          borderRadius: 10,
                          fontSize: 15,
                          background: C.surface
                        }}
                      >
                        <option value="concluido">Concluido</option>
                        <option value="cursando">Cursando</option>
                        <option value="trancado">Trancado</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <Input
                      label="Ano de inicio"
                      type="number"
                      value={formacao.anoInicio}
                      onChange={(e) => handleFormacaoChange(index, "anoInicio", e.target.value)}
                      placeholder="2020"
                    />
                    <Input
                      label="Ano de conclusao"
                      type="number"
                      value={formacao.anoConclusao}
                      onChange={(e) => handleFormacaoChange(index, "anoConclusao", e.target.value)}
                      placeholder="2024"
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={addFormacao}
              style={{
                width: "100%",
                padding: "14px",
                border: `2px dashed ${C.border}`,
                borderRadius: 12,
                background: "transparent",
                color: C.roseMid,
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                marginBottom: 24
              }}
            >
              + Adicionar outra formacao
            </button>

            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
              <Btn variant="secondary" onClick={() => setEtapa(1)} style={{ padding: "12px 32px" }}>
                Voltar
              </Btn>
              <Btn variant="primary" onClick={() => setEtapa(3)} style={{ padding: "12px 32px" }}>
                Proximo
              </Btn>
            </div>
          </div>
        )}

        {/* ETAPA 3: Experiencias e Avaliacoes */}
        {etapa === 3 && (
          <div>
            <div style={{
              background: "#FEF3C7",
              borderRadius: 12,
              padding: 16,
              marginBottom: 24,
              display: "flex",
              alignItems: "flex-start",
              gap: 12
            }}>
              <span style={{ fontSize: 20 }}>⚠️</span>
              <div>
                <p style={{ fontSize: 14, fontWeight: 600, color: "#92400E", marginBottom: 4 }}>
                  Avaliacao obrigatoria
                </p>
                <p style={{ fontSize: 13, color: "#92400E" }}>
                  Para cada empresa onde voce trabalhou, e obrigatorio avaliar a experiencia.
                  Isso ajuda outras mulheres a tomarem decisoes mais informadas sobre suas carreiras.
                </p>
              </div>
            </div>

            {experiencias.map((exp, expIndex) => (
              <div key={expIndex} style={{
                background: C.surface,
                borderRadius: 16,
                padding: 32,
                border: `1px solid ${exp.avaliacao.avaliado ? "#22C55E" : C.border}`,
                marginBottom: 24
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 600, color: C.textMain }}>
                    Experiencia {expIndex + 1}
                  </h3>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {exp.avaliacao.avaliado && (
                      <span style={{
                        background: "#D1FAE5",
                        color: "#065F46",
                        padding: "4px 12px",
                        borderRadius: 20,
                        fontSize: 12,
                        fontWeight: 500
                      }}>
                        Avaliado
                      </span>
                    )}
                    {experiencias.length > 1 && (
                      <button
                        onClick={() => removeExperiencia(expIndex)}
                        style={{
                          background: "#FEE2E2",
                          color: "#991B1B",
                          border: "none",
                          borderRadius: 6,
                          padding: "4px 10px",
                          fontSize: 12,
                          cursor: "pointer"
                        }}
                      >
                        Remover
                      </button>
                    )}
                  </div>
                </div>

                {/* Dados da experiencia */}
                <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 32 }}>
                  <Input
                    label="Nome da empresa"
                    value={exp.empresa}
                    onChange={(e) => handleExperienciaChange(expIndex, "empresa", e.target.value)}
                    required
                  />
                  <Input
                    label="Cargo"
                    value={exp.cargo}
                    onChange={(e) => handleExperienciaChange(expIndex, "cargo", e.target.value)}
                    required
                  />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <Input
                      label="Data de inicio"
                      type="month"
                      value={exp.dataInicio}
                      onChange={(e) => handleExperienciaChange(expIndex, "dataInicio", e.target.value)}
                    />
                    <div>
                      <Input
                        label="Data de saida"
                        type="month"
                        value={exp.dataFim}
                        onChange={(e) => handleExperienciaChange(expIndex, "dataFim", e.target.value)}
                        disabled={exp.atual}
                      />
                      <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={exp.atual}
                          onChange={(e) => handleExperienciaChange(expIndex, "atual", e.target.checked)}
                        />
                        <span style={{ fontSize: 13, color: C.textSec }}>Trabalho atual</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: C.textSec, marginBottom: 6 }}>
                      Descricao das atividades
                    </label>
                    <textarea
                      value={exp.descricao}
                      onChange={(e) => handleExperienciaChange(expIndex, "descricao", e.target.value)}
                      rows={3}
                      placeholder="Descreva suas principais atividades e responsabilidades..."
                      style={{
                        width: "100%",
                        padding: "12px 14px",
                        border: `1px solid ${C.border}`,
                        borderRadius: 10,
                        fontSize: 15,
                        resize: "vertical",
                        fontFamily: "inherit"
                      }}
                    />
                  </div>
                </div>

                {/* AVALIACAO DA EMPRESA */}
                <div style={{
                  background: C.background,
                  borderRadius: 12,
                  padding: 24,
                  border: `2px solid ${exp.avaliacao.avaliado ? "#22C55E" : C.roseMid}`
                }}>
                  <h4 style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: C.roseMid,
                    marginBottom: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 8
                  }}>
                    <span>⭐</span> Avalie esta empresa
                  </h4>

                  {/* Nota */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: C.textSec, marginBottom: 8 }}>
                      Nota geral (obrigatorio)
                    </label>
                    <div style={{ display: "flex", gap: 8 }}>
                      {[1, 2, 3, 4, 5].map(n => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => handleAvaliacaoChange(expIndex, "nota", n)}
                          style={{
                            width: 44,
                            height: 44,
                            borderRadius: 8,
                            border: `2px solid ${exp.avaliacao.nota === n ? C.roseMid : C.border}`,
                            background: exp.avaliacao.nota === n ? C.roseMid : C.surface,
                            color: exp.avaliacao.nota === n ? "#fff" : C.textMain,
                            fontSize: 16,
                            fontWeight: 600,
                            cursor: "pointer",
                            transition: "all 0.2s ease"
                          }}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Recomenda? */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: C.textSec, marginBottom: 8 }}>
                      Voce recomendaria esta empresa para outras mulheres?
                    </label>
                    <div style={{ display: "flex", gap: 12 }}>
                      <button
                        type="button"
                        onClick={() => handleAvaliacaoChange(expIndex, "recomenda", true)}
                        style={{
                          padding: "10px 24px",
                          borderRadius: 8,
                          border: `2px solid ${exp.avaliacao.recomenda === true ? "#22C55E" : C.border}`,
                          background: exp.avaliacao.recomenda === true ? "#D1FAE5" : C.surface,
                          color: exp.avaliacao.recomenda === true ? "#065F46" : C.textSec,
                          fontSize: 14,
                          fontWeight: 500,
                          cursor: "pointer"
                        }}
                      >
                        Sim, recomendo
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAvaliacaoChange(expIndex, "recomenda", false)}
                        style={{
                          padding: "10px 24px",
                          borderRadius: 8,
                          border: `2px solid ${exp.avaliacao.recomenda === false ? "#EF4444" : C.border}`,
                          background: exp.avaliacao.recomenda === false ? "#FEE2E2" : C.surface,
                          color: exp.avaliacao.recomenda === false ? "#991B1B" : C.textSec,
                          fontSize: 14,
                          fontWeight: 500,
                          cursor: "pointer"
                        }}
                      >
                        Nao recomendo
                      </button>
                    </div>
                  </div>

                  {/* Valores da empresa */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: C.textSec, marginBottom: 12 }}>
                      Quais valores voce identifica nesta empresa? (selecione os que se aplicam)
                    </label>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                      {/* Carreira */}
                      <div style={{ background: C.surface, borderRadius: 12, padding: 16, border: `1px solid ${C.border}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                          <span style={{ fontSize: 20 }}>💼</span>
                          <span style={{ fontSize: 14, fontWeight: 600, color: C.roseMid }}>Carreira</span>
                        </div>
                        {[
                          { id: "equidade_salarial", label: "Equidade salarial" },
                          { id: "mulheres_lideranca", label: "Mulheres na lideranca" },
                          { id: "crescimento", label: "Crescimento" }
                        ].map(valor => (
                          <label key={valor.id} style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 0",
                            cursor: "pointer",
                            fontSize: 13,
                            color: C.textSec
                          }}>
                            <input
                              type="checkbox"
                              checked={(exp.avaliacao.valores || []).includes(valor.id)}
                              onChange={(e) => {
                                const atuais = exp.avaliacao.valores || [];
                                const novosValores = e.target.checked
                                  ? [...atuais, valor.id]
                                  : atuais.filter(v => v !== valor.id);
                                handleAvaliacaoChange(expIndex, "valores", novosValores);
                              }}
                              style={{ accentColor: C.roseMid }}
                            />
                            {valor.label}
                          </label>
                        ))}
                      </div>

                      {/* Qualidade de vida */}
                      <div style={{ background: C.surface, borderRadius: 12, padding: 16, border: `1px solid ${C.border}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                          <span style={{ fontSize: 20 }}>🧠</span>
                          <span style={{ fontSize: 14, fontWeight: 600, color: C.roseMid }}>Qualidade de vida</span>
                        </div>
                        {[
                          { id: "flexibilidade", label: "Flexibilidade" },
                          { id: "trabalho_hibrido", label: "Trabalho hibrido" },
                          { id: "bem_estar", label: "Bem-estar" }
                        ].map(valor => (
                          <label key={valor.id} style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 0",
                            cursor: "pointer",
                            fontSize: 13,
                            color: C.textSec
                          }}>
                            <input
                              type="checkbox"
                              checked={(exp.avaliacao.valores || []).includes(valor.id)}
                              onChange={(e) => {
                                const atuais = exp.avaliacao.valores || [];
                                const novosValores = e.target.checked
                                  ? [...atuais, valor.id]
                                  : atuais.filter(v => v !== valor.id);
                                handleAvaliacaoChange(expIndex, "valores", novosValores);
                              }}
                              style={{ accentColor: C.roseMid }}
                            />
                            {valor.label}
                          </label>
                        ))}
                      </div>

                      {/* Apoio */}
                      <div style={{ background: C.surface, borderRadius: 12, padding: 16, border: `1px solid ${C.border}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                          <span style={{ fontSize: 20 }}>👩‍👧</span>
                          <span style={{ fontSize: 14, fontWeight: 600, color: C.roseMid }}>Apoio</span>
                        </div>
                        {[
                          { id: "maternidade", label: "Maternidade" },
                          { id: "familia", label: "Familia" },
                          { id: "ambiente_saudavel", label: "Ambiente saudavel" }
                        ].map(valor => (
                          <label key={valor.id} style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 0",
                            cursor: "pointer",
                            fontSize: 13,
                            color: C.textSec
                          }}>
                            <input
                              type="checkbox"
                              checked={(exp.avaliacao.valores || []).includes(valor.id)}
                              onChange={(e) => {
                                const atuais = exp.avaliacao.valores || [];
                                const novosValores = e.target.checked
                                  ? [...atuais, valor.id]
                                  : atuais.filter(v => v !== valor.id);
                                handleAvaliacaoChange(expIndex, "valores", novosValores);
                              }}
                              style={{ accentColor: C.roseMid }}
                            />
                            {valor.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Comentario */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: C.textSec, marginBottom: 6 }}>
                      Como foi sua experiencia nesta empresa?
                    </label>
                    <textarea
                      value={exp.avaliacao.comentario}
                      onChange={(e) => handleAvaliacaoChange(expIndex, "comentario", e.target.value)}
                      rows={3}
                      placeholder="Compartilhe sua experiencia trabalhando nesta empresa..."
                      style={{
                        width: "100%",
                        padding: "12px 14px",
                        border: `1px solid ${C.border}`,
                        borderRadius: 10,
                        fontSize: 14,
                        resize: "vertical",
                        fontFamily: "inherit"
                      }}
                    />
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: C.textSec, marginBottom: 6 }}>
                        Ponto positivo
                      </label>
                      <input
                        type="text"
                        value={exp.avaliacao.pontoPositivo}
                        onChange={(e) => handleAvaliacaoChange(expIndex, "pontoPositivo", e.target.value)}
                        placeholder="Ex: Flexibilidade de horario"
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          border: `1px solid ${C.border}`,
                          borderRadius: 8,
                          fontSize: 14
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: C.textSec, marginBottom: 6 }}>
                        Ponto negativo
                      </label>
                      <input
                        type="text"
                        value={exp.avaliacao.pontoNegativo}
                        onChange={(e) => handleAvaliacaoChange(expIndex, "pontoNegativo", e.target.value)}
                        placeholder="Ex: Comunicacao interna"
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          border: `1px solid ${C.border}`,
                          borderRadius: 8,
                          fontSize: 14
                        }}
                      />
                    </div>
                  </div>

                  <p style={{ fontSize: 12, color: C.textSec, marginBottom: 16 }}>
                    Sua avaliacao e publicada de forma anonima. O nome da empresa aparece, o seu nao.
                  </p>

                  {!exp.avaliacao.avaliado && (
                    <Btn
                      variant="primary"
                      onClick={() => {
                        if (exp.avaliacao.nota === 0) {
                          alert("Por favor, selecione uma nota antes de confirmar a avaliacao.");
                          return;
                        }
                        marcarAvaliado(expIndex);
                      }}
                      style={{ width: "100%", padding: "12px" }}
                    >
                      Confirmar avaliacao desta empresa
                    </Btn>
                  )}
                </div>
              </div>
            ))}

            <button
              onClick={addExperiencia}
              style={{
                width: "100%",
                padding: "16px",
                border: `2px dashed ${C.border}`,
                borderRadius: 12,
                background: "transparent",
                color: C.roseMid,
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                marginBottom: 24
              }}
            >
              + Adicionar outra experiencia profissional
            </button>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Btn variant="secondary" onClick={() => setEtapa(2)} style={{ padding: "12px 32px" }}>
                Voltar
              </Btn>
              <Btn
                variant="primary"
                onClick={() => {
                  if (!todasExperienciasAvaliadas()) {
                    alert("Por favor, avalie todas as empresas onde voce trabalhou antes de continuar.");
                    return;
                  }
                  setEtapa(4);
                }}
                style={{ padding: "12px 32px" }}
              >
                Proximo
              </Btn>
            </div>
          </div>
        )}

        {/* ETAPA 4: Finalizar */}
        {etapa === 4 && (
          <div style={{
            background: C.surface,
            borderRadius: 16,
            padding: 40,
            border: `1px solid ${C.border}`,
            textAlign: "center"
          }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              background: "#D1FAE5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
              fontSize: 40
            }}>
              ✓
            </div>

            <h2 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 28,
              fontWeight: 700,
              color: C.roseMid,
              marginBottom: 12
            }}>
              Quase la!
            </h2>
            <p style={{ fontSize: 15, color: C.textSec, marginBottom: 32, maxWidth: 400, margin: "0 auto 32px" }}>
              Revise suas informacoes e finalize seu cadastro. Voce avaliou {experiencias.length} empresa(s)
              e ajudou outras mulheres a conhecerem melhor o mercado de trabalho.
            </p>

            {/* Resumo */}
            <div style={{
              background: C.background,
              borderRadius: 12,
              padding: 24,
              textAlign: "left",
              marginBottom: 32
            }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: C.textSec, marginBottom: 16, textTransform: "uppercase", letterSpacing: 1 }}>
                Resumo do cadastro
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: C.textSec }}>Formacoes:</span>
                  <span style={{ fontWeight: 500, color: C.textMain }}>{formacoes.length}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: C.textSec }}>Experiencias:</span>
                  <span style={{ fontWeight: 500, color: C.textMain }}>{experiencias.length}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: C.textSec }}>Avaliacoes a enviar:</span>
                  <span style={{ fontWeight: 500, color: "#22C55E" }}>{experiencias.filter(e => e.avaliacao.avaliado).length}</span>
                </div>
              </div>
            </div>

            {/* Erro ao salvar */}
            {erro && (
              <div style={{
                background: "#FEE2E2",
                border: "1px solid #FCA5A5",
                borderRadius: 12,
                padding: 16,
                marginBottom: 24,
                textAlign: "left"
              }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: "#991B1B", marginBottom: 4 }}>
                  Nao conseguimos salvar seu cadastro
                </p>
                <p style={{ fontSize: 13, color: "#991B1B" }}>
                  {erro}
                </p>
                <p style={{ fontSize: 13, color: "#991B1B", marginTop: 8 }}>
                  Seus dados continuam preenchidos nesta tela. Tente novamente clicando em Finalizar cadastro.
                </p>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
              <Btn
                variant="secondary"
                onClick={() => setEtapa(3)}
                disabled={salvando}
                style={{ padding: "14px 32px", opacity: salvando ? 0.6 : 1 }}
              >
                Voltar e revisar
              </Btn>
              <Btn
                variant="primary"
                onClick={finalizarCadastro}
                disabled={salvando}
                style={{ padding: "14px 40px", opacity: salvando ? 0.6 : 1 }}
              >
                {salvando ? "Salvando..." : "Finalizar cadastro"}
              </Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
